import { GAME_LAYOUT } from "./settings";
import { WILD_SYMBOL_ID, COIN_SYMBOL_IDS } from "./SymbolData";

// Feature configuration
export const FEATURES_CONFIG = {
  FREE_SPINS: {
    ENABLED: false,
  },
  TENSION: {
    ENABLED: true, // Enable tension/anticipation mode
    TRIGGER_SYMBOL: COIN_SYMBOL_IDS, // Array of symbol IDs that trigger tension (e.g., COIN_SYMBOL_IDS for all coins)
    MIN_SYMBOLS: 4, // Minimum trigger symbols found to trigger anticipation mode
    CUTOFF_THRESHOLD: 6, // Stop tension mode when this many trigger symbols are reached (triggers bonus game)
    DURATION: 2000, // Duration each tension reel spins (milliseconds) - time from playTensionIn() call to reel stop: tension_in (667ms) + visible idle spin (~1333ms)
    COIN_GENERATION_PROBABILITY: 0.3, // Probability of generating coins during tension mode (45% vs 3.5% normal)
    SPEED_MULTIPLIER: 1.0, // Speed multiplier for tension spins (2.0 = 2x faster, 0.5 = 2x slower)
    POST_TENSION_TRANSITION_BUFFER: 600, // Extra delay (ms) for first reel after tension ends (smooth transition)
    // Native spine animation durations (from tension.json):
    // tension_in: 667ms, tension_idle: 1000ms (loops), tension_out: 1000ms
    SPIN_SLOWDOWN_MULTIPLIER: 3.0, // [DEPRECATED - Use SPEED_MULTIPLIER] Multiplier applied to base speed to slow tension spins
    STOP_SLOWDOWN_FACTOR: 0.39, // Stop speed relative to tension spin speed (0.5 = 50% speed = slower stop to match spin speed)
    REEL_STOP_SOUND_DELAY: 150, // Delay in milliseconds before playing reel stop sound when stopping from tension mode
    // Tint color for non-coin symbols during tension mode (black overlay effect)
    // Hex values for different opacity levels:
    // 0xffffff = 0% darkening (normal)
    // 0xcccccc = 20% darkening
    // 0xb3b3b3 = 30% darkening
    // 0x999999 = 40% darkening
    // 0x808080 = 50% darkening
    // 0x666666 = 60% darkening
    // 0x4d4d4d = 70% darkening
    // 0x333333 = 80% darkening
    // 0x1a1a1a = 90% darkening
    // 0x000000 = 100% darkening (fully black)
    NON_COIN_TINT: 0x999999, // Current: 40% darkening (~70% brightness)
  },

  WIN_LINES: {
    ENABLED: true,
  },
  // Win animation timing configuration
  // All timing values derive from the spine animation loop duration
  WIN_ANIMATION_TIMING: {
    SPINE_LOOP_DURATION: 1500, // Base duration for spine animation full loop (in ms)
    WIN_LINES_DELAY: 100, // Delay before showing win animations after spin completes (in ms)
    // Derived values (calculated automatically):
    get HALF_LOOP() {
      return this.SPINE_LOOP_DURATION / 2; // 750ms - half of spine loop
    },
    get LINE_CYCLE_DURATION() {
      return this.SPINE_LOOP_DURATION; // 1500ms - full spine loop per line
    },
    // Big win animation delay - adapts based on whether "show all wins" is active
    // Delay is exactly half of the animation duration (750ms = half of 1500ms spine loop)
    get BIG_WIN_DELAY_WITH_SHOW_ALL() {
      return this.HALF_LOOP; // 750ms - wait half animation after show all wins completes
    },
    get BIG_WIN_DELAY_WITHOUT_SHOW_ALL() {
      return this.HALF_LOOP; // 750ms - wait half animation after win lines start
    },
  },
  SHOW_ALL_WINS: {
    ENABLED: true, // If true: shows all winning symbols at once with global win text before line cycling (works for any win type)
    get DISPLAY_DURATION() {
      return FEATURES_CONFIG.WIN_ANIMATION_TIMING.SPINE_LOOP_DURATION; // 1500ms - matches spine loop
    },
    EXCLUDE_SCATTER: true, // If true: scatter wins skip "show all wins" and go directly to line cycling
  },
  EXPANDING_WILDS: {
    ENABLED: true, // If false: expanding wild feature is completely disabled, win lines start immediately after spin
    ANIMATION_MODE: "full_reel", // "cascade" | "full_reel" | "instant" - cascade: symbol-by-symbol, full_reel: spine overlay, instant: no animation
    CASCADE_DELAY: 86, // Delay in ms between each symbol in cascading wild animation
    FULL_REEL_ANIMATION_DURATION: 800, // Total duration for full-reel expanding wild animation in milliseconds.
    // This includes the "in" animation (~300ms) plus idle time (500ms) before win animations begin.
    // The animation plays: "in" (non-looping) → "idle" (looping) → "win" (when win detected)
    SHOW_ALL_WINS_BEFORE_CYCLING: true, // If true: shows all winning symbols after expanding wild animation (overrides SHOW_ALL_WINS.ENABLED)
    // If false: uses SHOW_ALL_WINS.ENABLED setting to determine behavior
    get ALL_WINS_DISPLAY_DURATION() {
      return FEATURES_CONFIG.WIN_ANIMATION_TIMING.SPINE_LOOP_DURATION; // 2000ms - matchesspine loop
    },
  },
  TURBO_SUGGESTION: {
    ENABLED: false, // If false: turbo suggestion popup is completely disabled, no manual stop tracking, no popup blocking
  },
  TURBO: {
    SPIN_SENSITIVITY: 400, // Time to hold button before turbo mode activates (ms)
  },
  SPIN: {
    // Wind-up animation settings
    WIND_UP_DURATION: 300, // Duration of wind-up animation per reel (ms) - set to 0 to disable wind-up
    REEL_START_DELAY: 75, // Delay between each reel starting (staggered effect, ms)
    WIND_UP_CHECK_BUFFER: 50, // Safety buffer for wind-up completion check (ms)

    // Stop delay settings
    STOP_DELAY_MANUAL: 16, // Delay between reels for manual stop (0 = instant/simultaneous stop)
    STOP_DELAY_AUTO: 250, // Delay between reels for automatic stop (cascade effect)

    // Anti-spam delays for non-winning spins (ms)
    // Prevents button spam by delaying setSpinComplete after reel animations finish
    // Winning spins are protected by counter animation instead
    SPIN_COMPLETE_DELAY: {
      REGULAR: 150, // Delay before setSpinComplete for regular speed
      TURBO: 330,   // Delay before setSpinComplete for turbo speed
    },

    // Auto-play speed mode settings
    AUTO_PLAY_SPEEDS: {
      AUTO: {
        SPIN_DURATION: 1000, // Animation loop duration for regular spinning
        SPEED_MS_PER_SYMBOL_HEIGHT: 40, // Milliseconds to travel one symbol height (higher = slower)
        BOUNCE_OVERSHOOT: 1.2, // Subtle bounce overshoot for smooth auto speed stopping
        STOP_SLOWDOWN_FACTOR: 0.5, // Stop speed relative to spin speed (1.0 = same speed = seamless transition, 0.9 = 90% speed = slower stop)
        AUTO_PLAY_DELAY: 500, // Delay between auto-play spins
        AUTO_STOP_TIMEOUT: 500, // Time before automatic stop starts
        REEL_STOP_SOUND_DELAY: 100, // Delay in milliseconds before playing reel stop sound
      },
      QUICK: {
        SPIN_DURATION: 150, // Animation loop duration for quick spinning
        SPEED_MS_PER_SYMBOL_HEIGHT: 26, // Milliseconds to travel one symbol height (higher = slower)
        BOUNCE_OVERSHOOT: 1, // Strong bounce overshoot for quick speed feel
        STOP_SLOWDOWN_FACTOR: 1.35, // Stop speed relative to spin speed (1.0 = same speed = seamless transition, 0.9 = 90% speed = slower stop)
        AUTO_PLAY_DELAY: 300, // Delay between auto-play spins
        AUTO_STOP_TIMEOUT: 300, // Time before automatic stop starts
        REEL_STOP_SOUND_DELAY: 100, // Delay in milliseconds before playing reel stop sound
      },
      TURBO: {
        DROP_DURATION: 250, // Drop animation duration
        BOUNCE_OVERSHOOT: 1.35, // Bounce overshoot factor (changed from 1.4 to 1.5 for half symbol bounce)
        SPIN_DELAY: 330, // Delay between turbo spins (combined with SPIN_COMPLETE_DELAY.TURBO = 660ms total)
        AUTO_PLAY_DELAY: 16, // Delay between auto-play spins (legacy, use SPIN_DELAY instead)
        AUTO_STOP_TIMEOUT: 16, // Time before automatic stop starts
        MIN_SPIN_DURATION: 600, // Minimum total spin time for consistent visual timing (optimized for fast servers)
        MIN_SERVER_WAIT: 350, // Minimum wait time before allowing drop (prevents too-fast drops)
        REEL_STOP_SOUND_DELAY: 100, // Delay in milliseconds before playing reel stop sound
      },
    },
  },
  HOLD_AND_WIN: {
    ENABLED: true, // If false: hold and win feature is completely disabled, no intro dialog will be shown
    INTRO_DELAY: 500, // Delay in ms before showing hold and win intro dialog after reels stop
    // Spin configuration
    SPEED_MS_PER_SYMBOL_HEIGHT: 134, // Milliseconds to move one symbol height (lower = faster)
    BLURRED_TEXTURES_ENABLED: true, // If true: use blurred textures during spin, if false: use regular textures
    // Stop animation configuration
    STOP_SLOWDOWN_FACTOR: 0.33, // Stop speed relative to spin speed (lower = slower stop, 0.35 = ~3x slower than spin)
    BOUNCE_OVERSHOOT: 0.9, // Bounce effect on landing (0 = no bounce, 1.2 = medium bounce, 2.0 = strong bounce)
    // Stop timing configuration
    SPIN_DURATION: 500, // Total spin duration before automatic stop (milliseconds) - needs to be long enough for API response
    SYMBOL_STOP_BASE_DELAY: 0, // Base delay before first symbol stops in cascading (milliseconds)
    SYMBOL_STOP_CASCADE_DELAY: 300, // Delay between each symbol stop in cascading for automatic stops (milliseconds)
    SYMBOL_STOP_CASCADE_DELAY_MANUAL: 50, // Manual stop cascade delay for TURBO mode (milliseconds)
    SYMBOL_STOP_CASCADE_DELAY_MANUAL_NORMAL: 25, // Manual stop cascade delay for NORMAL/AUTO speed (milliseconds) - faster for better responsiveness
    // Autoplay configuration
    AUTOPLAY_FIRST_SPIN_DELAY: 800, // Delay after transition completes before first autoplay spin starts in normal mode (milliseconds)
    AUTOPLAY_FIRST_SPIN_DELAY_TURBO: 800, // Delay for turbo mode - quick start after transition (milliseconds)
    AUTOPLAY_SPIN_DELAY: 800, // Delay between autoplay spins in normal mode (milliseconds)
    AUTOPLAY_SPIN_DELAY_TURBO: 625, // Delay between autoplay spins in turbo mode (half of normal speed)
    // Tension mode configuration
    TENSION: {
      ENABLED: true, // Enable tension/anticipation mode for Hold and Win
      COIN_THRESHOLD: 19, // Trigger when this many coins are on reelset (remaining blanks = 20 - COIN_THRESHOLD)
      COIN_GENERATION_PROBABILITY: 0.3, // Probability of generating coins during tension (30% vs 12.5% normal)
      DURATION: 3500, // Duration that symbol spins in tension mode before stopping (milliseconds) - increased for more anticipation
      CASCADE_DELAY: 750, // Delay between each tension symbol stop in cascading (milliseconds)
      TURBO_DURATION: 800, // Shorter tension duration for turbo mode (milliseconds)
      TURBO_CASCADE_DELAY: 150, // Faster cascade delay for turbo tension mode (milliseconds)
    },
    // Turbo mode configuration
    TURBO: {
      SPIN_DURATION: 200, // Minimal spin duration in turbo - drop almost immediately after server response (milliseconds)
      DROP_DURATION: 500, // Drop animation duration - matches regular stop feel (milliseconds)
      BOUNCE_OVERSHOOT: 1.0, // No bounce - lands exactly in place (1.0 = no overshoot)
      SYMBOL_STOP_CASCADE_DELAY: 65, // Cascade delay between symbols in turbo mode (milliseconds)
    },
    // Collection phase configuration
    COLLECTION: {
      TRIGGER_DELAY: 1000, // Delay before starting collection after conditions are met (milliseconds)
      INTRO_ANIMATION_DELAY: 750, // Wait time for collector intro animation (milliseconds)
      SYMBOL_COLLECT_DELAY: 750, // Delay between collecting each symbol (milliseconds)
      MULTIPLIER_UPDATE_DELAY: 50, // Delay after multiplier animation completes to show the multiplied value before fading (milliseconds)
      FINISH_DELAY: 500, // Delay after all symbols collected before finishing (milliseconds)
      OUTRO_DELAY: 1000, // Delay before triggering outro after collection finishes (milliseconds)
      // Counter animation durations
      COUNTER_ANIMATION_PER_COIN: 300, // Counter animation duration per coin in normal mode (milliseconds)
      COUNTER_ANIMATION_SKIP_MODE: 2000, // Counter animation duration in skip mode (milliseconds)
      COUNTER_ANIMATION_MULTIPLIER: 1000, // Counter animation duration for progress bar multiplier (milliseconds)
      ESTIMATED_TIME_PER_COIN: 1600, // Estimated time per coin for continuous counter animation (milliseconds)
    },
  },
  // Win threshold multipliers - centralized constants for win type determination
  WIN_THRESHOLDS: {
    // Animated win thresholds - ordered: BIG, MEGA, SUPER_MEGA
    BIG_WIN_THRESHOLD: 20, // 20x bet size
    MEGA_WIN_THRESHOLD: 40, // 40x bet size
    SUPER_MEGA_WIN_THRESHOLD: 60, // 60x bet size
  },
  // Win celebration animation configuration
  WIN_CELEBRATION: {
    AUTO_CLOSE_DELAY: 3000, // Auto-close delay after counter finishes (milliseconds) - popup closes automatically if user doesn't interact
  },
};

// Win type string constants - used throughout the codebase for win type identification
export const WIN_TYPES = {
  BIG_WIN: "big_win",
  MEGA_WIN: "mega_win",
  SUPER_MEGA_WIN: "super_mega_win",
};

// Centralized mapping of win types to their thresholds
export const WIN_TYPE_THRESHOLDS = {
  [WIN_TYPES.BIG_WIN]: FEATURES_CONFIG.WIN_THRESHOLDS.BIG_WIN_THRESHOLD,
  [WIN_TYPES.MEGA_WIN]: FEATURES_CONFIG.WIN_THRESHOLDS.MEGA_WIN_THRESHOLD,
  [WIN_TYPES.SUPER_MEGA_WIN]:
    FEATURES_CONFIG.WIN_THRESHOLDS.SUPER_MEGA_WIN_THRESHOLD,
};

// Expanding wild functions - cascading wild animations
/**
 * Build cascade sequences dynamically based on Settings.SYMBOLS_VISIBLE
 * Returns sequences for each possible wild position
 */
export const buildCascadeSequences = () => {
  const visibleCount = GAME_LAYOUT.SYMBOLS_VISIBLE;
  const sequences = {};

  for (let wildPos = 0; wildPos < visibleCount; wildPos++) {
    const sequence = [wildPos]; // Start with wild position

    if (wildPos === 0) {
      // Wild at top → cascade down
      for (let i = 1; i < visibleCount; i++) {
        sequence.push(i);
      }
    } else if (wildPos === visibleCount - 1) {
      // Wild at bottom → cascade up
      for (let i = visibleCount - 2; i >= 0; i--) {
        sequence.push(i);
      }
    } else {
      // Wild in middle → fill one direction first, then the other
      const isLowerHalf = wildPos >= Math.floor(visibleCount / 2);

      if (isLowerHalf) {
        // Fill below first, then above
        for (let i = wildPos + 1; i < visibleCount; i++) {
          sequence.push(i);
        }
        for (let i = wildPos - 1; i >= 0; i--) {
          sequence.push(i);
        }
      } else {
        // Fill above first, then below
        for (let i = wildPos - 1; i >= 0; i--) {
          sequence.push(i);
        }
        for (let i = wildPos + 1; i < visibleCount; i++) {
          sequence.push(i);
        }
      }
    }

    sequences[wildPos] = sequence;
  }

  return sequences;
};

export const EXPANDING_WILD_SEQUENCES = buildCascadeSequences();

/**
 * Detect which middle reels (1, 2, 3) have wild symbols when there's a win
 * @param {Object} currentRoundRef - Ref containing current round data
 * @param {Array} wins - Array of winning combinations from currentRound.wins
 * @returns {Array<{reelIndex: number, wildPosition: number}>} Array of objects with reel index and wild position
 */
export const detectExpandingWilds = (currentRoundRef, wins) => {
  const expandedReels = currentRoundRef.current?.reels;
  if (!expandedReels || !wins || !Array.isArray(wins) || wins.length === 0) {
    return [];
  }

  // Use wild symbol ID from settings (calculated once at module load)
  if (WILD_SYMBOL_ID === null) {
    return [];
  }

  const middleReelIndices = [1, 2, 3];
  const reelsWithWilds = [];

  for (const reelIndex of middleReelIndices) {
    const visibleSymbols = expandedReels[reelIndex];
    if (!visibleSymbols || !Array.isArray(visibleSymbols)) {
      continue;
    }

    // Server sends only visible symbols: [0, 1, 2, 3] (4 symbols, no buffers)
    // Use directly without slicing

    // Find FIRST wild position in visible symbols (searching for w1 by symbol ID)
    const wildPosition = visibleSymbols.findIndex(
      (symbolId) => symbolId === WILD_SYMBOL_ID
    );

    // If wild found, add it (no need to check if it's part of winning combination)
    if (wildPosition !== -1) {
      reelsWithWilds.push({
        reelIndex,
        wildPosition, // This is overlay position (0-3)
      });
    }
  }

  return reelsWithWilds;
};

/**
 * Get the appear animation duration for expanding wilds
 * @param {Object} overlayReelController - Controller to get animation duration
 * @returns {number} Appear animation duration in milliseconds (default: 1000ms)
 */
export const getAppearAnimationDuration = (overlayReelController) => {
  // Default fallback: 1000ms
  let appearAnimationDuration = 1000;

  if (overlayReelController) {
    // Use wild symbol ID from settings (calculated once at module load)
    if (WILD_SYMBOL_ID === null) {
      return appearAnimationDuration;
    }

    // Try to get duration from a spine instance
    const spineKey = `0_${WILD_SYMBOL_ID}`; // First position, w1 wild symbol
    const spineInstance =
      overlayReelController.prePositionedSpines?.get(spineKey);

    if (spineInstance?.skeleton?.data?.animations) {
      const appearAnimation = spineInstance.skeleton.data.animations.find(
        (anim) => anim.name.toLowerCase().includes("appear")
      );
      if (appearAnimation?.duration) {
        appearAnimationDuration = Math.round(appearAnimation.duration * 1000);
      }
    }
  }

  return appearAnimationDuration;
};

/**
 * Calculate the total duration for cascading expanding wild animation
 * @param {number} wildPosition - Position where wild was detected (0-3)
 * @param {Object} overlayReelController - Controller to get animation duration
 * @returns {number} Total duration in milliseconds
 */
export const calculateCascadeDuration = (
  wildPosition,
  overlayReelController
) => {
  // Get the sequence for this wild position
  const sequence =
    EXPANDING_WILD_SEQUENCES[wildPosition] ||
    Array.from({ length: GAME_LAYOUT.SYMBOLS_VISIBLE }, (_, i) => i);

  // Get appear animation duration using helper function
  const appearAnimationDuration = getAppearAnimationDuration(
    overlayReelController
  );

  // Calculate total cascade time:
  // - Delays between symbols: (sequence.length - 1) * CASCADE_DELAY
  // - Plus appear animation duration for the last symbol
  const cascadeDelayTime =
    (sequence.length - 1) * FEATURES_CONFIG.EXPANDING_WILDS.CASCADE_DELAY;
  const totalDuration = cascadeDelayTime + appearAnimationDuration;

  return totalDuration;
};

/**
 * Play cascading expanding wild animation for a single reel
 * @param {number} reelIndex - Index of the reel to animate
 * @param {number} wildPosition - Position where wild was detected (0-3)
 */
export const playCascadingExpandingWild = async (
  reelIndex,
  wildPosition,
  reelRefs,
  overlayReelRefs,
  currentTimeoutsRef
) => {
  const reels = reelRefs.current;
  const overlayReels = overlayReelRefs.current;

  if (!reels || !overlayReels) {
    return;
  }

  const reelRef = reels[reelIndex];
  const overlayReelRef = overlayReels[reelIndex];

  if (!reelRef || !overlayReelRef) {
    return;
  }

  const reelController = reelRef?.getReelController?.();
  const overlayReelController = overlayReelRef?.getOverlayReelController?.();

  if (!reelController || !overlayReelController) {
    return;
  }

  // Use wild symbol ID from settings (calculated once at module load)
  if (WILD_SYMBOL_ID === null) {
    return;
  }

  // Get the sequence for this wild position
  const sequence =
    EXPANDING_WILD_SEQUENCES[wildPosition] ||
    Array.from({ length: GAME_LAYOUT.SYMBOLS_VISIBLE }, (_, i) => i);

  // Play animations in sequence with delays
  for (let i = 0; i < sequence.length; i++) {
    const symbolIndex = sequence[i];

    // Show overlay appear animation at this position (using w1 wild symbol)
    overlayReelController.showAppearAnimationAtPosition(
      symbolIndex,
      WILD_SYMBOL_ID
    );

    // Update reel texture (in RAF for smooth rendering) - replace with w1 wild
    requestAnimationFrame(() => {
      reelController.replaceSymbolAtPosition(symbolIndex, WILD_SYMBOL_ID);
    });

    // Wait before next symbol (except for the last one)
    if (i < sequence.length - 1) {
      await new Promise((resolve) => {
        const timeoutId = setTimeout(
          resolve,
          FEATURES_CONFIG.EXPANDING_WILDS.CASCADE_DELAY
        );
        currentTimeoutsRef.current.push(timeoutId);
      });
    }
  }
};

/**
 * Get the "in" animation duration for expanding wild full-reel animation
 * @param {Object} overlayReelController - Controller to get animation duration
 * @returns {number} "in" animation duration in milliseconds (default: 300ms)
 */
export const getExpandingWildInAnimationDuration = (overlayReelController) => {
  // Default fallback: 300ms (typical "in" animation duration)
  let inAnimationDuration = 300;

  if (overlayReelController?.expandingWildSpine?.skeleton?.data?.animations) {
    const inAnimation =
      overlayReelController.expandingWildSpine.skeleton.data.animations.find(
        (anim) => anim.name === "in"
      );
    if (inAnimation?.duration) {
      inAnimationDuration = Math.round(inAnimation.duration * 1000);
    }
  }

  return inAnimationDuration;
};

/**
 * Play full-reel expanding wild animation for a single reel
 * @param {number} reelIndex - Index of the reel to animate
 * @param {Object} reelRefs - Refs to reel components (for texture replacement)
 * @param {Object} overlayReelRefs - Refs to overlay reel components
 * @param {Array} currentTimeoutsRef - Array to track timeouts for cleanup
 * @returns {Promise} Resolves when animation completes
 */
export const playFullReelExpandingWild = async (
  reelIndex,
  reelRefs,
  overlayReelRefs,
  currentTimeoutsRef
) => {
  const reels = reelRefs.current;
  const overlayReels = overlayReelRefs.current;

  if (!overlayReels || !reels) {
    return;
  }

  const reelRef = reels[reelIndex];
  const overlayReelRef = overlayReels[reelIndex];

  if (!overlayReelRef || !reelRef) {
    return;
  }

  // Get controllers
  const overlayReelController = overlayReelRef?.getOverlayReelController?.();

  // Play the full-reel animation (appear → idle)
  // This will initialize the spine if needed
  overlayReelRef.playExpandingWildFullReel?.();

  // Calculate actual "in" animation duration dynamically
  const inAnimationDuration = getExpandingWildInAnimationDuration(
    overlayReelController
  );

  // Wait for "in" animation to complete before resolving
  // This allows the animation to play while other reels animate in parallel
  return new Promise((resolve) => {
    const timeoutId = setTimeout(() => {
      resolve();
    }, inAnimationDuration);
    currentTimeoutsRef.current.push(timeoutId);
  });
};
